import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  dictionaries,
  translate,
  type Dictionaries,
  type Locale,
  type TranslateParams,
} from "./registry";
import { readLocale, writeLocale, type LocaleStorageGetter } from "./storage";
import type { MessageKey } from "./useT";

export interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: MessageKey, params?: TranslateParams) => string;
}

export const I18nContext = createContext<I18nContextValue | null>(null);

interface I18nProviderProps {
  children: ReactNode;
  initialLocale?: Locale;
  getStorage?: LocaleStorageGetter;
  messages?: Dictionaries;
}

export function I18nProvider({
  children,
  initialLocale,
  getStorage,
  messages = dictionaries,
}: I18nProviderProps) {
  const [locale, setLocaleState] = useState<Locale>(
    () => initialLocale ?? (getStorage ? readLocale(getStorage) : readLocale()),
  );

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback(
    (next: Locale) => {
      setLocaleState(next);
      if (getStorage) writeLocale(next, getStorage);
      else writeLocale(next);
    },
    [getStorage],
  );

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      setLocale,
      t: (key, params) => translate(messages, locale, key, params),
    }),
    [locale, setLocale, messages],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
